import { useState, useEffect } from 'react';
import StaffCard from './StaffCard';
import { clubService } from '../../services/api';

const MyStaff = () => {
  const [staff, setStaff] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch staff data
  const fetchStaff = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await clubService.getMyClubStaff();
      if (response.success) {
        setStaff(response.data || []);
      } else {
        setError(response.error);
      }
    } catch (error) {
      setError("Failed to load staff data");
    } finally {
      setIsLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchStaff();
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div className="px-12 py-4">
        <div className="flex justify-between items-center mb-6">
          <div className="text-sm text-gray-500">Loading staff...</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, index) => (
            <div key={index} className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 animate-pulse">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
                  <div className="h-5 bg-gray-200 rounded w-24"></div>
                </div>
                <div className="w-6 h-6 bg-gray-200 rounded"></div>
              </div>
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded w-16"></div>
                <div className="h-4 bg-gray-200 rounded w-20"></div>
                <div className="bg-gray-100 rounded-lg p-3 space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-32"></div>
                  <div className="h-3 bg-gray-200 rounded w-40"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="px-12 py-4">
        <div className="flex justify-between items-center mb-6">
          <div className="text-sm text-gray-500">Staff members</div>
        </div>
        <div className="text-center py-12">
          <div className="text-red-500 mb-2">Failed to load staff</div>
          <div className="text-sm text-gray-500 mb-4">{error}</div>
          <button 
            onClick={fetchStaff}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-12 py-4">
      {/* Header with Staff Count */}
      <div className="flex justify-between items-center mb-6">
        {/* QAV5-501: rendered as ONE JS string (not 3 JSX children split across
            "{count}" / " staff member" / "s") so it lands in the DOM as a single
            text node — the scanner (TranslationContext.js createTreeWalker) only
            ever sees whole text nodes, and the old 3-node split made the
            hand-translated my-club.json row unreachable. Same pattern as the
            transfer-windows "{n} day window" / "1 day window" pair
            (DetailPanel.js:37/48/55): a plain JS string, singular literal vs
            plural template, no in-JSX ternary splitting the sentence. */}
        <div className="text-sm text-gray-500">
          {staff.length === 1 ? "1 staff member" : `${staff.length} staff members`}
        </div>
      </div>

      {/* Staff Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {staff.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            No staff members found
          </div>
        ) : (
          staff.map(member => (
            <StaffCard key={member.account_id || member.id} staff={member} />
          ))
        )}
      </div>
    </div>
  );
};

export default MyStaff; 